import React from "react";
import styled from "styled-components";
import { URL } from "../../constants/navigation";
import { COLOR_WELL_READ } from "../../constants/styles";
import { IPlacementWorksheetUnitOccupant } from "../../services/PlacementWorksheetApi";
import { Box } from "../../UI/Box";

import { Button } from "../../UI/Button";
import { Checkbox } from "../../UI/Checkbox";
import { Typography } from "../../UI/Typography";
import { Link } from "../Link";
import { Modal } from "../Modal";
import { Switch } from "../Switch";
import { useMatchHandler } from "./useMatchHandler";

export interface IProps {
  unitId: number;
  unitResidents: IPlacementWorksheetUnitOccupant[];
  matchGroupAtLimit: boolean;
}

const StyledBoldText = styled.div`
  font-weight: bold;
`;

const generateResidentFullName = (resident: IPlacementWorksheetUnitOccupant) =>
  `${resident.firstName} ${resident.lastName}`;

export const PlacementWorksheetConfirmWorksheetModal: React.FC<IProps> = (
  props
) => {
  const {
    accepted,
    isLockIn,
    isLoading,
    setAccepted,
    handleMatch,
    isAcc,
    handleSwitchChange,
    onHide,
    selectedResidents,
    isOverride,
    toggleOverride,
    handleGenderCOnfilctOverride,
    isGenderConflictView,
  } = useMatchHandler(props.unitId, props.unitResidents);

  const selectedResidentsFullNames = selectedResidents.map(
    generateResidentFullName
  );
  const unitResidentsFullNames = props.unitResidents?.map(
    generateResidentFullName
  );
  const openGenderSettings = () => window.open(URL.MATCH_RULES, "_blank");

  const openMatchRules = () => window.open(URL.MATCH_RULES, "_blank");

  const title = isGenderConflictView
    ? "Gender Settings Conflict With Placement"
    : "Confirm Match";

  const confirmButtonLabel = isGenderConflictView
    ? "Yes, Override and Place Users"
    : "Match";
  const cancelButtonLabel = isGenderConflictView
    ? "No, Cancel This Request"
    : "Cancel";

  const content = isGenderConflictView ? (
    "These users have different Gender Settings that are not allowed to be placed as roommates. Would you like to override these settings and place these users together?"
  ) : (
    <>
      You are about to match{" "}
      <strong>{selectedResidentsFullNames.join(", ")}</strong> with{" "}
      <strong>{unitResidentsFullNames?.join(", ")}</strong>. Please confirm.
    </>
  );

  const confirmAction = isGenderConflictView
    ? handleGenderCOnfilctOverride
    : handleMatch;

  return (
    <Modal
      title={title}
      actions={
        <>
          <Button appearance="subtle" onClick={onHide} isLoading={isLoading}>
            {cancelButtonLabel}
          </Button>
          <Button
            appearance="primary"
            onClick={confirmAction}
            isLoading={isLoading}
            isDisabled={!accepted && !isGenderConflictView}
          >
            {confirmButtonLabel}
          </Button>
        </>
      }
    >
      <Box px={3} py={2}>
        <Typography gutterBottom mb={2}>
          {content}
        </Typography>
        {!isGenderConflictView && (
          <>
            <Checkbox
              name="accept"
              isChecked={accepted}
              onChange={() => setAccepted(!accepted)}
              label={
                <>
                  I understand that if our{" "}
                  <Link onClick={openMatchRules}>Match Dates</Link> are active,
                  these residents will be able to see this match in their Matches tab.  
                  If matching has ended, they will not be able to see that this match has been made. 
                  <StyledBoldText>At no time will RoomSync send a message notifying these residents of this match. </StyledBoldText>
                </>
              }
            />
            {props.matchGroupAtLimit && selectedResidents?.length === 1 && props.unitResidents?.length === 1 && (<Typography my={1} fontWeight={600} color={COLOR_WELL_READ}>
              Max number of match groups has been reached for this Unit Type. Are you sure you want to create this match?
            </Typography>)}
            {!isAcc && (
              <Checkbox
                name="accept"
                isChecked={isOverride}
                onChange={toggleOverride}
                label={
                  <>
                    If this match conflicts with{" "}
                    <Link onClick={openGenderSettings}>Gender Settings</Link>,
                    override and match these users
                  </>
                }
              />
            )}
            <Switch
              label="Lock in Match Group"
              switchProps={{
                checked: isLockIn,
                onChange: handleSwitchChange,
              }}
            />
          </>
        )}
      </Box>
    </Modal>
  );
};

PlacementWorksheetConfirmWorksheetModal.defaultProps = {
  unitResidents: [],
};
